<script setup>
import {defineProps, watch, ref} from 'vue';
import {i18n} from 'base/js/i18n';

const props = defineProps({
  elementId: String,
  open: Boolean
});

const emit = defineEmits(['dialog-closed']);

const dialog = ref(null);

function openDialog() {
  dialog.value.showModal();
}

function closeDialog() {
  dialog.value.close();
}

function notifyParent() {
  // Emit an event to notify the parent component that the dialog has been closed.
  emit('dialog-closed');
}

// Expose the openDialog function.
defineExpose({
  openDialog
});

watch(() => props.open, (newVal) => {
  if (newVal) {
    openDialog();
  } else {
    closeDialog();
  }
});

function closeIfDialog(e) {
  if (e.target.tagName === 'DIALOG') {
    closeDialog();
  }
}

</script>

<template>
  <!-- Bootstrap 5 modal using Vue reactivity to open/close. Includes slots for title, body, and footer -->
  <dialog ref="dialog" :id="`simpleModal-${props.elementId}`" @click="closeIfDialog" @close="notifyParent()" closeby="any" class="container p-45">
    <article>
      <div class="close-button">
        <button type="button" :aria-label="i18n.get('Close')" @click="closeDialog()">
          <i class="fass fa-xmark"></i>
        </button>
      </div>
      <div class="content" tabindex="0" autofocus>
        <slot name="content"></slot>
      </div>
    </article>
    <footer>
      <slot name="footer"></slot>
    </footer>

  </dialog>
</template>

<style>
// Importing base bootstrap variables and functions with our variables overrideing defaults.
@import "base/sass/variables";
@import "bootstrap/scss/functions";
@import "base/sass/bs5-custom-variables";
@import "bootstrap/scss/mixins";
@import "bootstrap/scss/utilities";

// Styling the HTML5 dialog element to match the bootstrap modal.
dialog {
  position: fixed;
  margin: auto;
  inset: 0;
  max-inline-size: min(90vw, 1600px);
  max-block-size: min(80vh, 100%);
  overflow-y: auto;
  overflow-x: hidden;
  z-index: 1050;
  box-shadow: $lifted;
  border-radius: $border-radius-lg;
  border: none;

  &:not([open]) {
    pointer-events: none;
    opacity: 0;
  }
}

.content {
  @include make-row();
}

dialog::backdrop {
  background-image: linear-gradient(
      to bottom right,
      rgba(0, 0, 0, 0.8),
      rgba(0, 0, 0, 0.6),
      rgba(0, 0, 0, 0.8)
  );
  opacity: 0.75;
}


article, footer {
  @include make-container();
  width: fit-content;
}

// TODO: Make this a mixin.
.close-button button {
  align-items: center;
  background-color: var(--foreground);
  color: var(--background);
  border: 2px solid black;
  border-radius: 50%;
  box-shadow: 0 0 10px 0 rgba(0, 0, 0, .4);
  cursor: pointer;
  display: flex;
  font-size: 2rem;
  justify-content: center;
  margin: 0;
  padding: 0;
  right: 1rem;
  top: 1rem;
  position: absolute;

  &:hover, &:focus-visible, &:focus {
    color: #000;
    background-color: #fff;
    border: 2px solid #fff;
  }
}


// Overriding/including font awesome here because outside styles do not apply within a custom element.
.fass {
  font-family: var(--fa-style-family-sharp);
  font-style: normal;
}

.fa-xmark {
  &:before {
    content: "\f057";
  }
}

// Hide the default focus outline for the content area.
.content:focus, .content:focus-visible {
  outline: none;
}

</style>